import { X } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsDesktopViewport } from '../../../hooks/useIsDesktopViewport';
import { setOverflowForSidebar, setPageHiddenBehindModal } from '../../../utils/helper';
import styles from './styles.module.scss';

// Shared shell for the Zara full-screen modals (ZaraUpsellModal, and the
// premium first-time-in-Zara welcome modal) — both reuse the exact same
// hero photo / eyebrow / title / mobile-vs-desktop chrome per Figma
// ("we can reuse the top section of the Meet Zara! modal, the images are
// the same"), differing only in what renders below the title. Extracted so
// that shared top section can't drift between the two the way copy-pasting
// it would eventually allow.
// HORO-10599: `eyebrowKey`/`titleLeadKey`/`titleAccentKey` default to the
// original "Meet Zara!" hero so ZaraWelcomeModal (which never passes them)
// is unaffected — ZaraUpsellModal's repurposed "Continue with Zara" variant
// overrides them instead of forking the shell.
const ZaraModalShell = ({
    isVisible,
    onClose,
    children,
    eyebrowKey = 'zaraUpsell.eyebrow',
    titleLeadKey = 'zaraUpsell.titleLead',
    titleAccentKey = 'zaraUpsell.titleAccent'
}) => {
    const { t } = useTranslation();
    const isDesktop = useIsDesktopViewport();

    // Same background-scroll-lock split AuthModal.js uses: the mobile
    // full-bleed screen removes the real page from layout entirely
    // (nothing to accidentally scroll into behind it); the desktop card's
    // backdrop is a dimmed/blurred view of the real page, so that page has
    // to stay in layout and just gets its own scroll locked instead. No
    // scroll-position save/restore or visualViewport tracking here — those
    // exist in AuthModal only to protect a focused text input from the
    // mobile keyboard, and neither Zara modal has inputs.
    useEffect(() => {
        setPageHiddenBehindModal(isVisible && !isDesktop);
        setOverflowForSidebar(isVisible && isDesktop);
        return () => {
            setPageHiddenBehindModal(false);
            setOverflowForSidebar(false);
        };
    }, [isVisible, isDesktop]);

    if (!isVisible) {
        return null;
    }

    const Eyebrow = () => <span className={styles.eyebrow}>{t(eyebrowKey)}</span>;

    const Title = () => (
        <h2 className={styles.title}>
            {t(titleLeadKey)}
            {titleAccentKey && <em className={styles.titleAccent}>{t(titleAccentKey)}</em>}
        </h2>
    );

    if (isDesktop) {
        return (
            <div className={styles.desktopBackdrop} role="dialog" aria-modal="true">
                <div className={styles.desktopScrim} aria-hidden="true" />
                <div className={styles.desktopCard}>
                    <div className={styles.desktopLeft}>
                        <Eyebrow />
                        <Title />
                        {children}
                    </div>
                    <div className={styles.desktopRight}>
                        <button type="button" className={styles.closeBtnDesktop} aria-label={t('buttons.close')} onClick={onClose}>
                            <X size={20} color="#FFFFFF" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.mobileScreen} role="dialog" aria-modal="true">
            <div className={styles.hero}>
                <button type="button" className={styles.closeBtnMobile} aria-label={t('buttons.close')} onClick={onClose}>
                    <X size={24} color="#FFFFFF" />
                </button>
            </div>
            <div className={styles.mobileHeroText}>
                <Eyebrow />
                <Title />
            </div>
            <div className={styles.mobileContent}>{children}</div>
        </div>
    );
};

export default ZaraModalShell;
